import React, { useState, useEffect } from "react";
import { LayoutDashboard, Briefcase, Upload, History, ChevronDown, ChevronLeft, ChevronRight, BarChart3, Bell, GitCompare, Target, ShieldAlert, ShieldCheck, BookOpen, TrendingUp, TrendingDown, Landmark, Coins, LogOut, Sparkles, Wallet, Bitcoin, Layers, Banknote, ReceiptIndianRupee, WalletCards, Files, Award, CircleDollarSign, PieChart, SlidersHorizontal, Menu, X, Brain, Users, Building2, Plane } from "lucide-react";
import { AuthContext } from "./AuthGate";
import GlobalSearch from "./GlobalSearch";
import { getMarketIndices } from "../lib/api";

const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { id: "home", label: "Home Dashboard", icon: LayoutDashboard },
      { id: "business", label: "Business 360", icon: Building2 },
      { id: "networth", label: "Net Worth", icon: Wallet },
    ],
  },
  {
    label: "Wealth",
    items: [
      {
        id: "investments", label: "Investments", icon: PieChart,
        children: [
          { id: "dashboard", label: "Portfolio Overview", icon: LayoutDashboard },
          { id: "investment_journey", label: "Investment Journey", icon: BarChart3 },
          { id: "investment_performance", label: "Performance & Cash Flows", icon: CircleDollarSign },
          { id: "momentum_strategy", label: "52-Week High Strategy", icon: TrendingUp },
          { id: "momentum_analysis", label: "Momentum Stock Analysis", icon: Target },
          { id: "momentum_sleeve", label: "Momentum Sleeve", icon: WalletCards },
          { id: "us_momentum_analysis", label: "US Nasdaq Momentum", icon: TrendingUp },
          { id: "funds_intel", label: "Mutual Funds & ETFs", icon: Layers },
          { id: "crypto", label: "Crypto", icon: Bitcoin },
          { id: "dividends", label: "Dividends", icon: Coins },
          { id: "sectors", label: "Allocation & Sectors", icon: BarChart3 },
          { id: "debt_retirement", label: "Debt & Retirement", icon: Landmark },
        ],
      },
      { id: "assets", label: "Property & Other Assets", icon: Landmark },
      { id: "rewards", label: "Rewards", icon: Award },
    ],
  },
  {
    label: "Money",
    items: [
      {
        id: "money-overview", label: "Financial Overview", icon: CircleDollarSign,
        children: [
          { id: "money", label: "Overview", icon: LayoutDashboard },
          { id: "income", label: "Income", icon: Banknote },
          { id: "spending", label: "Spending", icon: ReceiptIndianRupee },
          { id: "cashflow", label: "Cash Flow", icon: WalletCards },
        ],
      },
      { id: "financial_statements", label: "Financial Statements", icon: BarChart3 },
      { id: "transactions", label: "Transaction Ledger", icon: BookOpen },
      { id: "manual_entries", label: "Financial Inputs", icon: Banknote },
      { id: "statement_imports", label: "Statement Imports", icon: Files },
    ],
  },
  {
    label: "Planning",
    items: [
      { id: "travel", label: "Travel 360", icon: Plane },
      { id: "relationships", label: "Relationships", icon: Users },
      {
        id: "family-planning", label: "Family Planning", icon: Users,
        children: [
          { id: "family_planning", label: "Overview", icon: LayoutDashboard },
          { id: "family_planning_members", label: "Family Members", icon: Users },
          { id: "family_planning_goals", label: "Household Goals", icon: Target },
          { id: "family_planning_retirement", label: "Retirement", icon: Landmark },
          { id: "family_planning_assumptions", label: "Assumptions", icon: SlidersHorizontal },
        ],
      },
      { id: "financial_memory", label: "Financial Memory", icon: Brain },
      { id: "tax_history", label: "Tax History", icon: ReceiptIndianRupee },
    ],
  },
  {
    label: "Decisions",
    items: [
      {
        id: "investment-intelligence", label: "Investment Intelligence", icon: ShieldCheck,
        children: [
          { id: "adviser", label: "Portfolio Adviser", icon: ShieldCheck },
          { id: "decisions", label: "Stock Recommendations", icon: Target },
          { id: "ideas", label: "AI Ideas", icon: Sparkles },
          { id: "alerts", label: "Alerts", icon: Bell },
          { id: "journal", label: "Trade Journal", icon: BookOpen },
        ],
      },
    ],
  },
  {
    label: "Settings",
    items: [
      {
        id: "data-settings", label: "Data & Connections", icon: SlidersHorizontal,
        children: [
          { id: "accounts", label: "Accounts & Connections", icon: Briefcase },
          { id: "import", label: "Imports", icon: Upload },
          { id: "sync", label: "Synchronisation Log", icon: History },
          { id: "mappings", label: "Categories & Mappings", icon: GitCompare },
          { id: "rules", label: "Rules", icon: ShieldAlert },
        ],
      },
    ],
  },
];

const groupContainsPage = (item, activePage) =>
  Array.isArray(item.children) && item.children.some(child => child.id === activePage);

export default function Layout({ children, activePage, onNavigate, onStockClick }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [indices, setIndices] = useState({});
  const [openGroups, setOpenGroups] = useState(() => new Set(["investments"]));

  useEffect(() => {
    const parent = NAV_SECTIONS.flatMap(section => section.items).find(item => groupContainsPage(item, activePage));
    if (parent) setOpenGroups(previous => new Set([...previous, parent.id]));
  }, [activePage]);

  useEffect(() => {
    setMobileOpen(false);
  }, [activePage]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [mobileOpen]);

  const navigate = (id) => {
    setMobileOpen(false);
    onNavigate(id);
  };

  const toggleGroup = (id) => {
    if (collapsed) setCollapsed(false);
    setOpenGroups(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await getMarketIndices();
        setIndices(data.indices || {});
      } catch {}
    };
    load();
    const i = setInterval(load, 120000); // refresh every 2 min
    return () => clearInterval(i);
  }, []);

  return (
    <div className="min-h-screen flex bg-[var(--bg-base)] overflow-x-hidden" data-testid="app-layout">
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-nav-backdrop"
        />
      )}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[min(86vw,19rem)] ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:static ${collapsed ? "md:w-16" : "md:w-60"} flex-shrink-0 border-r border-white/10 bg-[var(--bg-surface)] flex flex-col transition-[transform,width] duration-200 shadow-2xl md:shadow-none`} data-testid="sidebar">
        {/* Logo */}
        <div className="p-4 flex items-center gap-2.5 border-b border-white/10 min-h-14">
          <svg width="22" height="22" viewBox="0 0 48 48" fill="none" className="flex-shrink-0">
            <circle cx="24" cy="24" r="22" stroke="url(#sg)" strokeWidth="3" fill="none" />
            <path d="M28 14C21.4 14 16 19.4 16 26s5.4 12 12 12c3.3 0 6.3-1.3 8.5-3.5" stroke="url(#sg)" strokeWidth="3" strokeLinecap="round" fill="none" />
            <path d="M34.5 34.5l-3-3" stroke="url(#sg)" strokeWidth="3" strokeLinecap="round" />
            <circle cx="28" cy="26" r="3" fill="url(#sg)" />
            <defs><linearGradient id="sg" x1="0" y1="0" x2="48" y2="48"><stop offset="0%" stopColor="#00D4FF" /><stop offset="100%" stopColor="#007AFF" /></linearGradient></defs>
          </svg>
          <div className={`${collapsed ? "md:hidden" : ""} leading-none`}>
            <span className="text-sm font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Capital360</span>
            <div className="text-[7px] tracking-[0.25em] uppercase text-cyan-400/60 mt-0.5">From Signals to Decisions</div>
          </div>
          <button type="button" onClick={() => setMobileOpen(false)} className="ml-auto p-1.5 text-[var(--text-muted)] hover:text-white md:hidden" aria-label="Close navigation">
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-1 overflow-y-auto">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <div className={`${collapsed ? "md:hidden" : ""} px-4 pt-4 pb-1 text-[9px] uppercase tracking-[0.15em] text-[var(--text-muted)] font-semibold`}>
                  {section.label}
              </div>
              {collapsed && <div className="hidden md:block my-1 mx-3 border-t border-white/5" />}
              {section.items.map((item) => {
                const { id, label, icon: Icon, children } = item;
                const isGroup = Array.isArray(children);
                const groupActive = groupContainsPage(item, activePage);
                if (!isGroup) return <button key={id} onClick={() => navigate(id)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-[13px] transition-colors ${
                    activePage === id
                      ? "bg-white/5 text-white border-r-2 border-cyan-400"
                      : "text-[var(--text-secondary)] hover:text-white hover:bg-white/[0.02]"
                  }`}
                  data-testid={`nav-${id}`}
                  title={collapsed ? label : undefined}>
                  <Icon size={16} className="flex-shrink-0" />
                  <span className={collapsed ? "md:hidden" : ""}>{label}</span>
                </button>;
                const expanded = openGroups.has(id);
                return <div key={id}>
                  <button onClick={() => toggleGroup(id)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-[13px] transition-colors ${
                      groupActive ? "text-white bg-white/[0.035]" : "text-[var(--text-secondary)] hover:text-white hover:bg-white/[0.02]"
                    }`}
                    data-testid={`nav-group-${id}`}
                    title={collapsed ? label : undefined}>
                    <Icon size={16} className={`flex-shrink-0 ${groupActive ? "text-cyan-300" : ""}`} />
                    <span className={`${collapsed ? "md:hidden" : ""} flex-1 text-left`}>{label}</span>
                    <ChevronDown size={13} className={`${collapsed ? "md:hidden" : ""} transition-transform ${expanded ? "rotate-180" : ""}`}/>
                  </button>
                  {expanded && <div className={`${collapsed ? "md:hidden" : ""} relative ml-5 border-l border-white/10`}>
                    {children.map(({ id: childId, label: childLabel, icon: ChildIcon }) => <button
                      key={childId} onClick={() => navigate(childId)}
                      className={`w-full flex items-center gap-2.5 pl-5 pr-3 py-1.5 text-[12px] transition-colors ${
                        activePage === childId
                          ? "text-white bg-cyan-500/[0.07] border-r-2 border-cyan-400"
                          : "text-[var(--text-muted)] hover:text-white hover:bg-white/[0.02]"
                      }`}
                      data-testid={`nav-${childId}`}>
                      <ChildIcon size={13} className="flex-shrink-0"/>
                      <span>{childLabel}</span>
                    </button>)}
                  </div>}
                </div>;
              })}
            </div>
          ))}
        </nav>

        {/* Market Tickers */}
        {Object.keys(indices).length > 0 && (
          <div className={`${collapsed ? "md:hidden" : ""} border-t border-white/10 px-3 py-3 space-y-2`} data-testid="market-tickers">
            <div className="text-[8px] uppercase tracking-[0.15em] text-[var(--text-muted)] font-semibold">Markets</div>
            {Object.entries(indices).map(([name, d]) => (
              <div key={name} className="flex items-center justify-between">
                <span className="text-[11px] text-[var(--text-secondary)]">{name}</span>
                <div className="text-right">
                  <div className="font-mono text-[11px] text-white">{d.price?.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                  <div className={`font-mono text-[9px] flex items-center gap-0.5 justify-end ${d.change >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                    {d.change >= 0 ? <TrendingUp size={8} /> : <TrendingDown size={8} />}
                    {d.change >= 0 ? "+" : ""}{d.change_pct}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {collapsed && Object.keys(indices).length > 0 && (
          <div className="hidden md:block border-t border-white/10 py-2 space-y-1.5" data-testid="market-tickers-collapsed">
            {Object.entries(indices).map(([name, d]) => (
              <div key={name} className="flex flex-col items-center px-1" title={`${name}: ${d.price?.toLocaleString()} (${d.change >= 0 ? "+" : ""}${d.change_pct}%)`}>
                <div className={`w-2 h-2 rounded-full ${d.change >= 0 ? "bg-emerald-400" : "bg-red-400"}`} />
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-white/10">
          <button onClick={() => setCollapsed(!collapsed)} className="hidden md:flex w-full items-center justify-center py-2 text-[var(--text-muted)] hover:text-white transition-colors">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
          <AuthFooter collapsed={collapsed} mobileOpen={mobileOpen} />
        </div>
      </aside>

      <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
        <header className="h-14 border-b border-white/10 bg-[var(--bg-surface)] flex items-center px-3 sm:px-4 gap-2 sm:gap-4 flex-shrink-0" data-testid="top-bar">
          <button type="button" onClick={() => setMobileOpen(true)} className="md:hidden flex-shrink-0 p-2 -ml-1 rounded-lg text-[var(--text-secondary)] hover:text-white hover:bg-white/5" aria-label="Open navigation" data-testid="mobile-menu-button">
            <Menu size={19} />
          </button>
          <GlobalSearch onStockClick={onStockClick} />
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden" data-testid="app-main">{children}</main>
      </div>
    </div>
  );
}

function AuthFooter({ collapsed, mobileOpen }) {
  const { user, logout } = React.useContext(AuthContext);
  if (!user) return null;
  if (collapsed && !mobileOpen) {
    return (
      <button onClick={logout} className="w-full flex items-center justify-center py-2 text-[var(--text-muted)] hover:text-red-400 transition-colors" title={`Logout (${user.email})`} data-testid="logout-btn-collapsed">
        <LogOut size={14} />
      </button>
    );
  }
  const initial = (user.name || user.email || "?").charAt(0).toUpperCase();
  return (
    <div className="px-3 pb-3 pt-1 flex items-center gap-2" data-testid="auth-footer">
      {user.picture ? (
        <img src={user.picture} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
      ) : (
        <div className="w-6 h-6 rounded-full bg-[var(--brand)] text-white text-[10px] flex items-center justify-center font-semibold">{initial}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-white font-medium truncate">{user.name || user.email}</div>
        <div className="text-[9px] text-[var(--text-muted)] truncate">{user.email}</div>
      </div>
      <button onClick={logout} className="text-[var(--text-muted)] hover:text-red-400 p-1 flex-shrink-0" title="Logout" data-testid="logout-btn">
        <LogOut size={14} />
      </button>
    </div>
  );
}
